/**
 * FP3-104 — Manage an assistant's ORGANIZATION-LEVEL supervisors.
 *
 * Multi-select of compatible primary providers (SLPA→SLP, COTA→OT,
 * PTA→PT; unclassifiable roles stay eligible — same rule as roster
 * create). Works for rostered / invited / active assistants. The
 * plural `supervising_provider_ids` list is authoritative; a legacy
 * singular-only record initializes with that one supervisor selected.
 * Removing a supervisor still referenced by active student-level
 * assistant assignments is blocked by the backend with the affected
 * student list — surfaced here so the admin can reassign first.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WarningCircle, UsersThree } from "@phosphor-icons/react";
import api from "@/lib/api";
import { EXPECTED_FAMILY, familyOf } from "./CreateProviderModal";

export default function ManageSupervisorsModal({
  open,
  onClose,
  assistant,
  primaryProviders = [],
  onSaved,
}) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !assistant) return;
    // Part H — plural list is authoritative; a singular-only legacy
    // record loads with that one supervisor pre-selected.
    const initial =
      assistant.supervising_provider_ids &&
      assistant.supervising_provider_ids.length
        ? assistant.supervising_provider_ids
        : [assistant.supervising_provider_id].filter(Boolean);
    setSelected([...new Set(initial)]);
    setBlocked(null);
    setSaving(false);
    setQuery("");
  }, [open, assistant]);

  const options = useMemo(() => {
    if (!assistant) return [];
    const expected = EXPECTED_FAMILY[assistant.assistant_type] || "";
    const q = query.trim().toLowerCase();
    return primaryProviders
      .filter((p) => (p.provider_role_type || "primary") !== "assistant")
      .filter((p) => p.id !== assistant.id)
      .filter(
        (p) =>
          !["deactivated", "disabled"].includes(
            (p.account_status || "").toLowerCase()
          )
      )
      .filter((p) => {
        if (!expected) return true;
        const fam = familyOf(p.role);
        return !fam || fam === expected;
      })
      .filter(
        (p) =>
          !q ||
          (p.name || "").toLowerCase().includes(q) ||
          selected.includes(p.id)
      )
      .sort((a, b) => {
        // Selected supervisors surface first so the current state is
        // always visible without scrolling.
        const sa = selected.includes(a.id) ? 0 : 1;
        const sb = selected.includes(b.id) ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [primaryProviders, assistant, query, selected]);

  const toggle = (id) => {
    setBlocked(null);
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const save = async () => {
    if (!assistant) return;
    if (selected.length === 0) {
      toast.error("An assistant needs at least one supervising provider.");
      return;
    }
    setSaving(true);
    setBlocked(null);
    try {
      await api.updateRosterProvider(assistant.id, {
        supervising_provider_ids: selected,
      });
      toast.success("Supervisors updated");
      onSaved?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "supervisor_in_use") {
        setBlocked(detail);
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
            "Could not update supervisors"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-md max-h-[85vh] overflow-y-auto"
        data-testid="manage-supervisors-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-[#1F2937] flex items-center gap-2">
            <UsersThree size={16} weight="duotone" className="text-[#1E3F33]" />
            Manage supervisors
          </DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-[#6B7280] -mt-1">
          {assistant?.name}
          {assistant?.assistant_type ? ` · ${assistant.assistant_type}` : ""} —
          select every provider authorized to supervise this assistant. The
          per-student supervisor is chosen when assigning students.
        </p>
        <input
          type="text"
          data-testid="manage-supervisors-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search providers…"
          className="w-full h-8 px-2.5 text-[12.5px] rounded-lg border border-[#EDEAE0] focus:outline-none focus:ring-1 focus:ring-[#1E3F33]/40 placeholder:text-[#B7BCC4]"
        />
        <div
          className="space-y-1.5 max-h-64 overflow-y-auto border border-[#EDEAE0] rounded-lg p-2"
          data-testid="manage-supervisors-options"
        >
          {options.length === 0 && (
            <p
              data-testid="manage-supervisors-empty"
              className="text-[12px] text-[#9C6B2E] p-1"
            >
              No compatible supervising providers found
              {assistant?.assistant_type ? ` for ${assistant.assistant_type}` : ""}.
            </p>
          )}
          {options.map((p) => (
            <label
              key={p.id}
              data-testid={`manage-supervisors-option-${p.id}`}
              className="flex items-center gap-2 text-[12.5px] text-[#1F2937] px-1.5 py-1 rounded-md hover:bg-[#F8F7F2] cursor-pointer"
            >
              <input
                type="checkbox"
                data-testid={`manage-supervisors-checkbox-${p.id}`}
                checked={selected.includes(p.id)}
                onChange={() => toggle(p.id)}
                className="accent-[#1E3F33]"
              />
              <span className="flex-1 truncate">{p.name}</span>
              {p.role && (
                <span className="text-[10.5px] text-[#6B7280]">{p.role}</span>
              )}
            </label>
          ))}
        </div>
        {blocked && (
          <div
            data-testid="manage-supervisors-blocked"
            className="text-[11.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E5C9C2] rounded-lg p-2.5 leading-snug"
          >
            <div className="flex items-start gap-1.5">
              <WarningCircle size={13} weight="duotone" className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Can't remove yet.</strong>{" "}
                {blocked.message}
                <ul className="mt-1 list-disc list-inside">
                  {(blocked.blocked || []).map((b) => (
                    <li key={b.supervisor_id}>
                      {b.supervisor_name}: {b.student_count} student
                      {b.student_count === 1 ? "" : "s"}
                      {b.students?.length
                        ? ` (${b.students.join(", ")}${b.student_count > b.students.length ? ", …" : ""})`
                        : ""}
                    </li>
                  ))}
                </ul>
                Reassign those student-level relationships first (Student
                Assignments or each student's Team tab), then remove the
                supervisor.
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            data-testid="manage-supervisors-cancel"
            variant="outline"
            size="sm"
            onClick={() => onClose?.()}
            className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
          >
            Cancel
          </Button>
          <Button
            data-testid="manage-supervisors-save"
            size="sm"
            onClick={save}
            disabled={saving || selected.length === 0}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            {saving ? "Saving…" : `Save supervisors (${selected.length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
